import { Inbox, Layers } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { QueueResponse } from '@/api/client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface QueueSelectorProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	queues: QueueResponse[];
	currentQueueId?: number;
	onSelect: (queueId: number) => void;
}

// Virtual "Incoming" queue for display
const INCOMING_QUEUE: QueueResponse = {
	id: 0,
	slug: 'incoming',
	name: 'Incoming',
	color: '#6366f1',
	icon: null,
	position: -1,
};

export function QueueSelector({
	open,
	onOpenChange,
	queues,
	currentQueueId,
	onSelect,
}: QueueSelectorProps) {
	const [query, setQuery] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	// Combine incoming with user queues (filter out id=0 if API includes it)
	const allQueues = useMemo(() => {
		const userQueues = queues.filter((q) => q.id !== 0);
		return [INCOMING_QUEUE, ...userQueues];
	}, [queues]);

	// Filter queues by search query
	const filteredQueues = useMemo(() => {
		if (!query.trim()) return allQueues;
		const lowerQuery = query.toLowerCase();
		return allQueues.filter(
			(q) => q.name.toLowerCase().includes(lowerQuery) || q.slug.toLowerCase().includes(lowerQuery)
		);
	}, [allQueues, query]);

	// Focus input when dialog opens
	useEffect(() => {
		if (open) {
			setTimeout(() => inputRef.current?.focus(), 0);
			// Pre-select current queue if exists
			const currentIndex = allQueues.findIndex((q) => q.id === currentQueueId);
			setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);
		} else {
			setQuery('');
			setSelectedIndex(0);
		}
	}, [open, allQueues, currentQueueId]);

	// Reset selection when filter changes
	useEffect(() => {
		setSelectedIndex(0);
	}, []);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => Math.min(prev + 1, filteredQueues.length - 1));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex((prev) => Math.max(prev - 1, 0));
					break;
				case 'Enter':
					e.preventDefault();
					if (filteredQueues[selectedIndex]) {
						onSelect(filteredQueues[selectedIndex].id);
						onOpenChange(false);
					}
					break;
				case 'Escape':
					onOpenChange(false);
					break;
			}
		},
		[filteredQueues, selectedIndex, onSelect, onOpenChange]
	);

	const handleSelect = useCallback(
		(queueId: number) => {
			onSelect(queueId);
			onOpenChange(false);
		},
		[onSelect, onOpenChange]
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="top-[20%] translate-y-0 p-0 gap-0 overflow-hidden max-w-md"
			>
				{/* Search Input */}
				<div className="flex items-center border-b px-3">
					<Layers className="size-4 text-muted-foreground shrink-0" />
					<input
						ref={inputRef}
						type="text"
						placeholder="Search queues..."
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						className="flex-1 h-12 px-3 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
					/>
					<kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:flex">
						ESC
					</kbd>
				</div>

				{/* Queue List */}
				<ScrollArea className="max-h-[300px]">
					{filteredQueues.length === 0 && query.trim() && (
						<div className="py-6 text-center text-sm text-muted-foreground">No queues found.</div>
					)}
					{filteredQueues.length > 0 && (
						<div className="p-2">
							{filteredQueues.map((queue, index) => (
								<button
									key={queue.id}
									type="button"
									onClick={() => handleSelect(queue.id)}
									className={cn(
										'w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors',
										index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
										queue.id === currentQueueId && 'ring-1 ring-primary/50'
									)}
								>
									{queue.id === 0 ? (
										<Inbox className="size-4 shrink-0 text-muted-foreground" />
									) : (
										<span
											className="size-4 rounded-full shrink-0"
											style={{ backgroundColor: queue.color }}
										/>
									)}
									{queue.icon && <span className="text-base">{queue.icon}</span>}
									<span className="flex-1 font-medium text-sm">{queue.name}</span>
									{queue.linkCount !== undefined && (
										<span className="text-xs text-muted-foreground">{queue.linkCount}</span>
									)}
									{queue.id === currentQueueId && (
										<span className="text-xs text-muted-foreground">current</span>
									)}
								</button>
							))}
						</div>
					)}
				</ScrollArea>

				{/* Footer */}
				<div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
					<div className="flex items-center gap-2">
						<kbd className="px-1.5 py-0.5 rounded border bg-muted font-mono">↑↓</kbd>
						<span>Navigate</span>
						<kbd className="px-1.5 py-0.5 rounded border bg-muted font-mono">↵</kbd>
						<span>Select</span>
					</div>
					<div>
						{filteredQueues.length} queue{filteredQueues.length !== 1 ? 's' : ''}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
